import { Box } from "@mui/material";
import { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { LuZap, LuDroplet, LuCoins } from "react-icons/lu";
import EndOfLifeTreatmentTable from "../../../circularity-components/EndOfLifeTreatmentTable";
import InputMaterialTable from "../../../circularity-components/InputMaterialTable";
import OutputMaterialTable from "../../../circularity-components/OutputMaterialTable";
import UtilityFactorTable from "../../../circularity-components/UtilityFactorTable";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import { DataTableCard, M3Button, SegmentedButton } from "../../../components/m3";
import CircularityTableActions from "../../../circularity-components/CircularityTableActions";
import IndicatorPlaceholderPanel from "./IndicatorPlaceholderPanel";
import BuildingProductDropdown from "../../Building/components/BuildingProductDropdown";

// `selectedSubcategoryId`/`onSubcategoryChange` are Building Circularity-only (never
// passed by Product/Project). When present, they render BuildingProductDropdown before
// the tab switcher as a *controlled* component — the selection, the processes list, and
// the WBLCA fetch all live in BuildingCircularity.js (shared with the Material
// Composition and MCI Calculation tabs, so switching tabs keeps the same product
// selected and only one fetch happens). `combined_data` here is already scoped to the
// selected product by the parent, so this component needs no Building-specific fetching
// of its own — it behaves exactly as it always did for Product/Project.
const UtilityTable = ({ combined_data, activeTab, tabValue, setCircularities, materials, aiLoading, handleSmartFill, scopeEnergy, scopeWater, scopeEconomic, topic = "Product", selectedSubcategoryId = null, onSubcategoryChange = null }) => {
  const { t } = useTranslation();
  const circularities = useMemo(() => combined_data.combined_circularities, [combined_data]);
  const service_life = useMemo(() => combined_data.service_life || 0, [combined_data]);

  const utility = { label: "Utility", value: "utility", title: "Utility" };
  const input = { label: "Input", value: "input", title: "Input Material" };
  const output = { label: "Output", value: "output", title: "Output Material" };
  const end_of_life = { label: "End of Life", value: "end-of-life", title: "End of Life Treatment" };
  const energy = { label: "Energy", value: "energy", title: "Energy Circularity" };
  const water = { label: "Water", value: "water", title: "Water Circularity" };
  const economic = { label: "Economic", value: "economic", title: "Economic Circularity" };

  const dataTabValues = [utility.value, input.value, output.value, end_of_life.value];
  const tabs = [
    utility, input, output, end_of_life,
    ...(scopeEnergy ? [energy] : []),
    ...(scopeWater ? [water] : []),
    ...(scopeEconomic ? [economic] : []),
  ];
  const [selectedTab, setSelectedTab] = useState(utility.value);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const gridHeaderContainerRef = useRef(null);

  // Close menu when switching main tabs
  useEffect(() => {
    if (activeTab && tabValue && activeTab !== tabValue) {
      setColumnMenuVisible(false);
    }
  }, [activeTab, tabValue]);

  // Close menu when switching sub-tabs
  useEffect(() => {
    setColumnMenuVisible(false);
  }, [selectedTab]);

  // If an indicator's sub-tab is open and its toggle gets switched off in Scope & Boundary, fall back to Utility
  useEffect(() => {
    if (!tabs.some((tb) => tb.value === selectedTab)) {
      setSelectedTab(utility.value);
    }
  }, [scopeEnergy, scopeWater, scopeEconomic]);


  // We will pass these down to children if needed, or children will handle their own.
  // For now, let's let each sub-table handle its own visibility but we provide the button.

  return (
    <DataTableCard
      title={t(tabs.find((tab) => tab.value === selectedTab)?.title || "")}
      leading={
        <SegmentedButton
          size="sm"
          showCheck={false}
          options={tabs.map((tab) => ({ value: tab.value, label: t(tab.label) }))}
          value={selectedTab}
          onChange={setSelectedTab}
          ariaLabel={t("Utility sections")}
        />
      }
      actions={
        <CircularityTableActions
          columnsAnchorRef={gridHeaderContainerRef}
          onToggleColumns={dataTabValues.includes(selectedTab) ? () => setColumnMenuVisible((p) => !p) : null}
          showRefresh={false}
        >
          {onSubcategoryChange && (
            <BuildingProductDropdown value={selectedSubcategoryId} onChange={onSubcategoryChange} />
          )}
          {handleSmartFill && selectedTab !== input.value && dataTabValues.includes(selectedTab) && (
            <M3Button
              size="sm"
              variant="outlined"
              icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />}
              loading={aiLoading}
              onClick={() => handleSmartFill(circularities, materials)}
              disabled={!materials || materials.length === 0}
              disabledReason={t("Add materials to estimate circularity values")}
            >
              {aiLoading ? t("Estimating...") : t("Smart Fill with AI")}
            </M3Button>
          )}
        </CircularityTableActions>
      }
    >
      <Box sx={{ display: selectedTab === utility.value ? "block" : "none" }}>
        <UtilityFactorTable
          circularities={circularities}
          service_life={service_life}
          columnMenuVisible={columnMenuVisible && selectedTab === utility.value}
          setColumnMenuVisible={setColumnMenuVisible}
          anchorRef={gridHeaderContainerRef}
          setCircularities={setCircularities}
          topic={topic}
        />
      </Box>
      <Box sx={{ display: selectedTab === input.value ? "block" : "none" }}>
        <InputMaterialTable
          circularities={circularities}
          columnMenuVisible={columnMenuVisible && selectedTab === input.value}
          setColumnMenuVisible={setColumnMenuVisible}
          anchorRef={gridHeaderContainerRef}
          setCircularities={setCircularities}
        />
      </Box>
      <Box sx={{ display: selectedTab === output.value ? "block" : "none" }}>
        <OutputMaterialTable
          circularities={circularities}
          columnMenuVisible={columnMenuVisible && selectedTab === output.value}
          setColumnMenuVisible={setColumnMenuVisible}
          anchorRef={gridHeaderContainerRef}
          setCircularities={setCircularities}
        />
      </Box>
      <Box sx={{ display: selectedTab === end_of_life.value ? "block" : "none" }}>
        <EndOfLifeTreatmentTable
          circularities={circularities}
          columnMenuVisible={columnMenuVisible && selectedTab === end_of_life.value}
          setColumnMenuVisible={setColumnMenuVisible}
          anchorRef={gridHeaderContainerRef}
          setCircularities={setCircularities}
          topic={topic}
        />
      </Box>
      {scopeEnergy && (
        <Box sx={{ display: selectedTab === energy.value ? "block" : "none" }}>
          <IndicatorPlaceholderPanel
            icon={LuZap}
            title="Energy Circularity"
            description="Energy indicator is enabled in Scope & Boundary. Renewable vs. non-renewable energy flow tracking for this product is not yet available."
          />
        </Box>
      )}
      {scopeWater && (
        <Box sx={{ display: selectedTab === water.value ? "block" : "none" }}>
          <IndicatorPlaceholderPanel
            icon={LuDroplet}
            title="Water Circularity"
            description="Water indicator is enabled in Scope & Boundary. Withdrawal, recycled and discharged water flow tracking for this product is not yet available."
          />
        </Box>
      )}
      {scopeEconomic && (
        <Box sx={{ display: selectedTab === economic.value ? "block" : "none" }}>
          <IndicatorPlaceholderPanel
            icon={LuCoins}
            title="Economic Circularity"
            description="Economic indicator is enabled in Scope & Boundary. Residual value and circular-revenue tracking for this product is not yet available."
          />
        </Box>
      )}
    </DataTableCard>
  );
};
export default UtilityTable;
